import {useState} from 'react'
import {useQuery} from '@tanstack/react-query'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {Stack} from '@/components/shared/Stack'
import {InlineRow} from '@/components/shared/InlineRow'
import {FormField} from '@/components/shared/FormField'
import {MutedText} from '@/components/shared/MutedText'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Checkbox} from '@/components/ui/checkbox'
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select'
import {Combobox, type ComboboxOption} from '@/components/ui/combobox'
import {NonResponseAction, ReviewerType} from '@/types/certification'
import type {WizardFormState} from '../hooks/useCertificationPolicyWizardForm'

export interface WizardStepReviewSettingsProps {
    form: WizardFormState
    onChange: <K extends keyof WizardFormState>(key: K, value: WizardFormState[K]) => void
}

/**
 * Review Settings step — reviewer type, frequency override, grace period,
 * non-response action, auto-revoke, and campaign owner. Narrow prop contract
 * (current form slice in, change handler out) per spec §3.6.
 */
export function WizardStepReviewSettings({form, onChange}: WizardStepReviewSettingsProps) {
    const i18n = useI18n()
    const services = useServices()
    const [ownerSearchQuery, setOwnerSearchQuery] = useState('')

    const {data: ownerSearchData, isLoading: ownerSearchLoading} = useQuery({
        queryKey: ['identities', 'search', ownerSearchQuery],
        queryFn: () => services.identities.list({q: ownerSearchQuery, size: 10}),
    })
    const {data: selectedOwner} = useQuery({
        queryKey: ['identities', form.ownerPartyId],
        queryFn: () => services.identities.detail(form.ownerPartyId!),
        enabled: !!form.ownerPartyId,
    })
    const ownerOptions: ComboboxOption[] = (ownerSearchData?.content || []).map(identity => ({
        value: identity.id,
        label: identity.party.displayName || identity.identityType,
        description: identity.identityType,
    }))

    return (
        <Stack gap="lg" data-help-prefix="certificationPolicyWizard">
            <FormField label={String(i18n.certificationPolicies.wizard.reviewerTypeLabel)} htmlFor="wizard-review-reviewer-type">
                <Select
                    value={form.reviewerType}
                    onValueChange={value => onChange('reviewerType', value as ReviewerType)}
                >
                    <SelectTrigger id="wizard-review-reviewer-type" data-help-key="reviewSettings.reviewerType" data-testid="select-reviewer-type">
                        <SelectValue/>
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={ReviewerType.MANAGER} data-testid="option-reviewer-manager"
                            description={i18n.certificationPolicies.wizard.reviewerTypeManagerDesc}>
                            {i18n.certificationPolicies.reviewerTypeManager}
                        </SelectItem>
                        <SelectItem value={ReviewerType.PROFILE_OWNER} data-testid="option-reviewer-profile-owner"
                            description={i18n.certificationPolicies.wizard.reviewerTypeProfileOwnerDesc}>
                            {i18n.certificationPolicies.reviewerTypeProfileOwner}
                        </SelectItem>
                        <SelectItem value={ReviewerType.RESOURCE_OWNER} data-testid="option-reviewer-resource-owner"
                            description={i18n.certificationPolicies.wizard.reviewerTypeResourceOwnerDesc}>
                            {i18n.certificationPolicies.reviewerTypeResourceOwner}
                        </SelectItem>
                    </SelectContent>
                </Select>
            </FormField>

            <Stack gap="sm">
                <InlineRow gap="md">
                    <Checkbox
                        id="wizard-review-override-frequency"
                        checked={form.overrideReviewFrequency}
                        onCheckedChange={checked => onChange('overrideReviewFrequency', checked === true)}
                        data-testid="checkbox-override-review-frequency"
                    />
                    <Label htmlFor="wizard-review-override-frequency">{i18n.certificationPolicies.overrideReviewFrequency}</Label>
                </InlineRow>
                <MutedText size="sm" block data-help-key="reviewSettings.frequency">
                    {i18n.certificationPolicies.wizard.useProfileFrequencyHint}
                </MutedText>
                {form.overrideReviewFrequency && (
                    <Input
                        type="number"
                        value={form.reviewFrequencyDays}
                        onChange={e => onChange('reviewFrequencyDays', parseInt(e.target.value, 10) || 0)}
                        placeholder={String(i18n.certificationPolicies.reviewFrequencyDaysPlaceholder)}
                        data-testid="input-review-frequency-days"
                    />
                )}
            </Stack>

            <FormField label={String(i18n.certificationPolicies.gracePeriodDays)} htmlFor="wizard-review-grace-period">
                <Input
                    id="wizard-review-grace-period"
                    type="number"
                    value={form.gracePeriodDays}
                    onChange={e => onChange('gracePeriodDays', parseInt(e.target.value, 10) || 0)}
                    placeholder={String(i18n.certificationPolicies.gracePeriodDaysPlaceholder)}
                    data-help-key="reviewSettings.gracePeriod"
                    data-testid="input-grace-period-days"
                />
                <MutedText size="xs">{i18n.certificationPolicies.wizard.gracePeriodHint}</MutedText>
            </FormField>

            <FormField label={String(i18n.certificationPolicies.nonResponseAction)} htmlFor="wizard-review-non-response">
                <Select
                    value={form.nonResponseAction}
                    onValueChange={value => onChange('nonResponseAction', value as NonResponseAction)}
                >
                    <SelectTrigger id="wizard-review-non-response" data-help-key="reviewSettings.nonResponseAction" data-testid="select-non-response-action">
                        <SelectValue/>
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={NonResponseAction.NO_CHANGE} data-testid="option-non-response-no-change">
                            {i18n.certificationPolicies.nonResponseNoChange}
                        </SelectItem>
                        <SelectItem value={NonResponseAction.AUTO_REVOKE} data-testid="option-non-response-auto-revoke">
                            {i18n.certificationPolicies.nonResponseAutoRevoke}
                        </SelectItem>
                        <SelectItem value={NonResponseAction.ESCALATE} data-testid="option-non-response-escalate">
                            {i18n.certificationPolicies.nonResponseEscalate}
                        </SelectItem>
                    </SelectContent>
                </Select>
            </FormField>

            <Stack gap="sm">
                <InlineRow gap="md">
                    <Checkbox
                        id="wizard-review-auto-revoke"
                        checked={form.autoRevokeOnOverdue}
                        onCheckedChange={checked => onChange('autoRevokeOnOverdue', checked === true)}
                        data-testid="checkbox-auto-revoke-on-overdue"
                    />
                    <Label htmlFor="wizard-review-auto-revoke">{i18n.certificationPolicies.autoRevokeOnOverdue}</Label>
                </InlineRow>
                <MutedText size="xs" block>{i18n.certificationPolicies.wizard.autoRevokeHint}</MutedText>
            </Stack>

            <FormField label={String(i18n.certificationPolicies.wizard.campaignOwnerLabel)} htmlFor="wizard-review-owner">
                <Combobox
                    options={ownerOptions}
                    value={form.ownerPartyId}
                    onValueChange={value => onChange('ownerPartyId', value)}
                    onSearch={setOwnerSearchQuery}
                    placeholder={i18n.certificationPolicies.searchOwner}
                    emptyMessage={i18n.certificationPolicies.noIdentitiesFound}
                    isLoading={ownerSearchLoading}
                    selectedLabel={selectedOwner?.party?.displayName || selectedOwner?.identityType}
                    data-testid="combobox-campaign-owner"
                />
                <MutedText size="xs">{i18n.certificationPolicies.campaignOwnerDesc}</MutedText>
            </FormField>
        </Stack>
    )
}
